import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, fmtINR, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toCSV, downloadCSV, inDateRange } from "@/lib/csv";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

const empty = { date: today(), party_id: "", type: "payment", amount: "", reference: "", notes: "" };

export default function LedgerPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [summary, setSummary] = useState([]);
  const [selected, setSelected] = useState("");
  const [txns, setTxns] = useState([]);
  const [parties, setParties] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const loadSummary = () => api.get("/ledger/summary").then((r) => setSummary(r.data));
  const loadTxns = (pid) => api.get(`/ledger${pid ? `?party_id=${pid}` : ""}`).then((r) => setTxns(r.data));

  useEffect(() => {
    loadSummary();
    loadTxns();
    api.get("/parties").then((r) => setParties(r.data));
  }, []);

  useEffect(() => { loadTxns(selected); }, [selected]);

  const startEdit = (t) => {
    setEditing(t.id);
    setF({ date: t.date, party_id: t.party_id, type: t.type, amount: t.amount, reference: t.reference || "", notes: t.notes || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, amount: Number(f.amount) };
      if (editing) {
        await api.put(`/ledger/${editing}`, payload);
        toast.success("Ledger entry updated");
      } else {
        await api.post("/ledger", payload);
        toast.success("Ledger entry recorded");
      }
      cancelEdit();
      loadSummary();
      loadTxns(selected);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this ledger entry?")) return;
    await api.delete(`/ledger/${id}`);
    loadSummary();
    loadTxns(selected);
  };

  const filteredTxns = useMemo(() => txns.filter((t) => inDateRange(t.date, from, to)), [txns, from, to]);

  const exportSummary = () => {
    const cols = [
      { key: "name", label: "Party" }, { key: "type", label: "Type" },
      { key: "billed", label: "Billed" }, { key: "paid", label: "Paid" }, { key: "balance", label: "Balance" }, { key: "pending_stitch_pcs", label: "Pending Pcs (stitching)" },
    ];
    downloadCSV(`slkc-ledger-summary-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(summary, cols));
  };

  const exportTxns = () => {
    const cols = [
      { key: "date", label: "Date" }, { key: "party_name", label: "Party" },
      { key: "type", label: "Type" }, { key: "amount", label: "Amount" },
      { key: "reference", label: "Reference" }, { key: "notes", label: "Notes" },
    ];
    downloadCSV(`slkc-ledger-${from || "all"}-${to || "today"}.csv`, toCSV(filteredTxns, cols));
  };

  return (
    <div>
      <PageHeader
        label="Finance"
        title="Party Ledger"
        subtitle="Auto-generated bills from yarn, batches, stitching · manual payments"
        right={<button onClick={exportSummary} data-testid="ledger-summary-csv" className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background hover:-translate-y-[1px] transition-transform">↓ Summary CSV</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card title="Party Balances" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="table-dense w-full">
              <thead><tr><th>Party</th><th>Type</th><th className="text-right">Billed</th><th className="text-right">Paid</th><th className="text-right">Balance</th><th className="text-right">Pending Pcs</th></tr></thead>
              <tbody>
                {summary.map((s) => (
                  <tr key={s.party_id} onClick={() => setSelected(s.party_id)} className="cursor-pointer" data-testid={`ledger-summary-${s.party_id}`}>
                    <td className="font-semibold">{s.name}</td>
                    <td><span className="label-caps">{s.type.replace("_", " ")}</span></td>
                    <td className="tabular text-right">{fmtINR(s.billed)}</td>
                    <td className="tabular text-right">{fmtINR(s.paid)}</td>
                    <td className={`tabular text-right font-bold ${s.balance > 0 ? "text-destructive" : "text-green-700"}`}>{fmtINR(s.balance)}</td>
                    <td className="tabular text-right" data-testid={`ledger-pending-${s.party_id}`}>
                      {(s.pending_stitch_pcs || 0) > 0 ? (
                        <span className="inline-block label-caps px-2 py-0.5 bg-rose-100 text-rose-800">{s.pending_stitch_pcs} pcs</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {summary.length === 0 && <tr><td colSpan={6}><Empty text="No parties yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title={editing ? "Edit Entry" : "Record Entry"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="ledger-form">
            <Field label="Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Party">
              <Select required value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })}>
                <option value="">Select…</option>
                {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Type">
              <Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                <option value="payment">Payment (out)</option>
                <option value="bill">Bill (in)</option>
              </Select>
            </Field>
            <Field label="Amount (₹)"><Input type="number" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
            <Field label="Reference"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
            <Btn data-testid="ledger-submit">{editing ? "Save Changes" : "Save Entry"}</Btn>
          </form>
        </Card>

        <Card title="Recent Transactions" right={
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 px-2 bg-white border border-border font-mono text-xs w-32" data-testid="ledger-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 px-2 bg-white border border-border font-mono text-xs w-32" data-testid="ledger-to" />
            <Select value={selected} onChange={(e) => setSelected(e.target.value)} className="w-40">
              <option value="">All parties</option>
              {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <button onClick={exportTxns} data-testid="ledger-txns-csv" className="h-8 px-3 font-mono text-[10px] tracking-[0.15em] uppercase font-bold bg-foreground text-background">↓ CSV</button>
          </div>
        }>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th>Party</th><th>Type</th><th className="text-right">Amount</th><th>Ref</th><th></th></tr></thead>
              <tbody>
                {filteredTxns.slice(0, 200).map((t) => (
                  <tr key={t.id} className={editing === t.id ? "bg-secondary" : ""}>
                    <td className="font-mono">{t.date}</td>
                    <td>{t.party_name}</td>
                    <td><span className={`label-caps ${t.type === "bill" ? "text-destructive" : "text-green-700"}`}>{t.type}</span></td>
                    <td className="tabular text-right">{fmtINR(t.amount)}</td>
                    <td className="font-mono text-xs">{t.reference || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button onClick={() => startEdit(t)} className="label-caps hover:text-foreground mr-3" data-testid={`ledger-edit-${t.id}`}>Edit</button>
                          <button onClick={() => del(t.id)} className="label-caps hover:text-destructive">Del</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredTxns.length === 0 && <tr><td colSpan={6}><Empty text="No transactions in range" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
